/**
 * ADA Fix: Improper Nesting of Buttons within Links
 * 
 * Issue: Buttons nested within links (<a><button></button></a>) cause confusion
 * for screen reader users who hear "button link" which is not semantically correct.
 * This is also invalid HTML.
 * 
 * Recommendation: Remove improper nesting by either:
 * - Converting buttons to styled links
 * - Converting the structure to use proper overlay links
 * - Removing redundant interactive elements
 * 
 * This script detects and fixes any remaining instances of nested buttons.
 */

(function() {
  'use strict';

  console.log('🔧 ADA Fix: Checking for improperly nested buttons...');

  /**
   * Find all buttons nested inside links
   */
  function findNestedButtons() {
    const nestedButtons = [];
    const allButtons = document.querySelectorAll('button, [role="button"]');

    allButtons.forEach(function(button) {
      // Check if button is inside a link
      let current = button.parentElement;
      let depth = 0;
      const maxDepth = 10;

      while (current && depth < maxDepth) {
        if (current.tagName === 'A' || current.getAttribute('role') === 'link') {
          nestedButtons.push({
            button: button,
            link: current,
            depth: depth
          });
          break;
        }
        current = current.parentElement;
        depth++;
      }
    });

    return nestedButtons;
  }

  /**
   * Fix a nested button by converting it to a styled span
   */
  function fixNestedButton(buttonInfo) {
    const button = buttonInfo.button;
    const link = buttonInfo.link;

    console.log('⚠️  Found nested button:', {
      buttonText: button.textContent.trim(),
      linkHref: link.href,
      buttonClasses: button.className
    });

    // Get button attributes
    const buttonClasses = button.className;
    const buttonText = button.innerHTML;
    const buttonId = button.id;
    const buttonAriaLabel = button.getAttribute('aria-label');

    // Create a span replacement (visually identical, but not interactive)
    const span = document.createElement('span');
    span.className = buttonClasses;
    span.innerHTML = buttonText;
    
    if (buttonId) {
      span.id = buttonId;
    }

    // Mark as presentation since the link handles interaction
    span.setAttribute('role', 'presentation');
    span.setAttribute('aria-hidden', 'true');

    // Replace button with span
    button.parentNode.replaceChild(span, button);

    // Ensure the link has an accessible label
    if (!link.getAttribute('aria-label') && !link.getAttribute('aria-labelledby')) {
      // Try to use the button's text or aria-label
      const label = buttonAriaLabel || buttonText.trim().replace(/<[^>]*>/g, '');
      if (label) {
        link.setAttribute('aria-label', label);
      }
    }

    console.log('✅ Fixed nested button: converted to span, link has aria-label');

    return {
      original: button,
      replacement: span,
      link: link
    };
  }

  /**
   * Process all nested buttons
   */
  function processNestedButtons() {
    const nestedButtons = findNestedButtons();

    if (nestedButtons.length === 0) {
      console.log('✅ No nested buttons found - all clear!');
      return [];
    }

    console.log(`⚠️  Found ${nestedButtons.length} nested button(s)`);

    const fixes = nestedButtons.map(fixNestedButton);

    console.log(`✅ Fixed ${fixes.length} nested button(s)`);

    return fixes;
  }

  /**
   * Validate fix: ensure no buttons remain inside links
   */
  function validateFix() {
    const remainingNestedButtons = findNestedButtons();

    if (remainingNestedButtons.length === 0) {
      console.log('✅ Validation passed: No nested buttons remaining');
      return true;
    } else {
      console.warn(`⚠️  Validation failed: ${remainingNestedButtons.length} nested button(s) still found`);
      return false;
    }
  }

  /**
   * Setup mutation observer for dynamically added content
   */
  function setupDynamicContentObserver() {
    const observer = new MutationObserver(function(mutations) {
      let shouldReprocess = false;

      mutations.forEach(function(mutation) {
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach(function(node) {
            if (node.nodeType === 1) {
              // Check if it's a button or contains buttons
              if (node.tagName === 'BUTTON' || 
                  (node.querySelector && node.querySelector('button'))) {
                shouldReprocess = true;
              }
            }
          });
        }
      });

      if (shouldReprocess) {
        setTimeout(function() {
          console.log('🔄 Reprocessing nested buttons due to DOM changes...');
          processNestedButtons();
        }, 200);
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    return observer;
  }

  /**
   * Initialize the fix
   */
  function initialize() {
    console.log('🚀 Initializing nested button fix...');

    // Process existing nested buttons
    const fixes = processNestedButtons();

    // Validate
    validateFix();

    // Setup observer for dynamic content
    setupDynamicContentObserver();

    console.log('✅ Nested button fix initialized');

    return fixes;
  }

  // Run on page load
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize);
  } else {
    initialize();
  }

  // Also run after a delay for dynamic content
  setTimeout(initialize, 1000);

  // Expose functions for testing
  window.adaNestedButtonFix = {
    findNestedButtons,
    fixNestedButton,
    processNestedButtons,
    validateFix,
    initialize
  };

  console.log('🔧 ADA Nested Button Fix loaded. Functions available in window.adaNestedButtonFix');

})();
